import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { updateQueueStatus } from '../../../../ReduxSlices/Prescriptions/prescriptionQueueSlice';

function PrescriptionQueueItem({ queue, error, medicationError}) {
  //console.log(medicationError);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  // Initialize quantity input fields using all medications
  // If quantity exists, use it; otherwise default to 1
  const [quantities, setQuantities] = useState(() =>
    Object.fromEntries(queue.all_medications.map((med) => [med.id, med.quantity || 1]))
  );

  // Handle change in quantity input for a specific medication
  const handleQuantityChange = (id, value) => {
    const qty = Math.max(1, parseInt(value, 10) || 1); // enforce minimum value of 1
    setQuantities((prev) => ({ ...prev, [id]: qty }));
  };

  const handleStatusUpdate = (status) => {
  setLoading(true);

  if (status === 'dispensed') {
    dispatch(updateQueueStatus({
      id: queue.id,
      status,
      prescription_queue_items_attributes: queue.new_medications.map(item => ({
        medication_item_id: item.id,
        quantity: quantities[item.id],
      })),
    })).finally(() => setLoading(false));
  } else {
    dispatch(updateQueueStatus({ id: queue.id, status }))
      .finally(() => setLoading(false));
  }
};

const meds = queue.status === 'pending' ? queue.new_medications : queue.all_medications;


  return (
    <div className="item w-full">
      {/* Patient Info */}
      <p>
        <strong>Patient:</strong> {queue.patient.name}
      </p>      

      {/* Medication List */}
      <div>
        <strong>Medications:</strong>
        <ul style={{ marginLeft: '1rem' }}>
          {meds && meds.length > 0 ? (
            meds.map((item) => (
              <li key={item.id} className="mb-2">
                   {medicationError[queue.id]?.includes(item.id) && (
                    <p className="error-msg">
                      ⚠️
                    </p>
                  )}
                {/* Medication details */}
                <strong>{item.medication_name}</strong> — {item.dosage}, {item.frequency},{' '}
                {item.duration}
                {item.instruction && ` (${item.instruction})`}

                {/* Show quantity input only when pending */}
                {queue.status === 'pending' && (
                  <div>
                    <label>
                      Quantity:{' '}
                      <input
                        type="number"
                        min="1"
                        className="border p-1 w-16 rounded"
                        value={quantities[item.id]}
                        onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                      />
                    </label>
                  </div>
                )} 
                           
              </li>
            ))
          ) : (
            <p className="italic text-sm text-gray-500">No medication items listed.</p>
          )}
        </ul>
      </div>

      {/* Queue Status and Actions */}
      <div>
        <p>
          <strong>Status:</strong> {queue.status}
        </p>

        {/* Show "Mark Dispensed" button only if status is pending and stock is sufficient */}
        {queue.status === 'pending' && queue.can_dispense && (
          <button disabled={loading} type="button" onClick={() => handleStatusUpdate('dispensed')}>
            Mark Dispensed
          </button>
        )}

        {/* Cancel button (always shown while pending) */}
        {queue.status === 'pending' && (
          <button
            disabled={loading}
            type="button"
            onClick={() => handleStatusUpdate('cancelled')}
            style={{ marginLeft: '10px' }}
          >
            Cancel
          </button>
        )}
     

        {/* If not enough stock, show warning */}
        {queue.status === 'pending' && !queue.can_dispense && (
          <p className="text-red-500">❌ Not enough stock to dispense</p>
        )}
      </div>

        {error && (
          <p className="error-msg">
            {typeof error === 'string' ? error : error.error}
          </p>
        )}

    </div>
  );
}

PrescriptionQueueItem.propTypes = {
  queue: PropTypes.shape({
    id: PropTypes.number.isRequired,
    status: PropTypes.string.isRequired,
    patient: PropTypes.shape({ name: PropTypes.string.isRequired }).isRequired,
    new_medications: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        medication_name: PropTypes.string.isRequired,
        dosage: PropTypes.string,
        frequency: PropTypes.string,
        duration: PropTypes.string,
        instruction: PropTypes.string,
        quantity: PropTypes.number,
      })
    ),
    all_medications: PropTypes.array,
    can_dispense: PropTypes.bool.isRequired,
  }).isRequired,
};

export default PrescriptionQueueItem;
